import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

import { TRACKING_IDS } from "./tracking-ids";

const LeadInput = z.object({
  service: z.string().trim().min(2).max(300),
  location: z.string().trim().min(2).max(300),
  name: z.string().trim().max(120).optional().nullable(),
  phone: z.string().trim().max(40).optional().nullable(),
  sourcePage: z.string().trim().max(300).optional().nullable(),
});

export const submitLead = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => LeadInput.parse(input))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");

    const { data: lead, error } = await supabaseAdmin
      .from("leads")
      .insert({
        service: data.service,
        location: data.location,
        name: data.name ?? null,
        phone: data.phone ?? null,
        source_page: data.sourcePage ?? null,
      })
      .select("id")
      .single();

    if (error) {
      console.error("Falha ao salvar lead:", error.message);
      return { ok: false as const, id: null };
    }

    // Encaminha para o Zapier quando configurado (não bloqueia o lead se falhar)
    try {
      const { data: settings } = await supabaseAdmin
        .from("private_settings")
        .select("zapier_webhook_url")
        .eq("id", "default")
        .maybeSingle();

      const url = settings?.zapier_webhook_url;
      if (url) {
        const res = await fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ type: "lead", ...data, id: lead.id, created_at: new Date().toISOString() }),
        });
        if (res.ok) {
          await supabaseAdmin.from("leads").update({ sent_to_zapier: true }).eq("id", lead.id);
        } else {
          console.error("Zapier respondeu", res.status, await res.text());
        }
      }
    } catch (err) {
      console.error("Erro ao enviar lead ao Zapier:", err);
    }

    return { ok: true as const, id: lead.id };
  });

export const getPublicSettings = createServerFn({ method: "GET" }).handler(async () => {
  const key = process.env["SUPABASE_PUBLISHABLE_KEY"]!;
  const { createClient } = await import("@supabase/supabase-js");
  const supabase = createClient(process.env["SUPABASE_URL"]!, key, {
    auth: { persistSession: false, autoRefreshToken: false },
    global: {
      fetch: (input, init) => {
        const headers = new Headers(init?.headers);
        if (key.startsWith("sb_") && headers.get("Authorization") === `Bearer ${key}`) {
          headers.delete("Authorization");
        }
        headers.set("apikey", key);
        return fetch(input, { ...init, headers });
      },
    },
  });

  // O painel administrativo tem prioridade; os IDs fixados no código servem de
  // reserva caso o banco esteja pausado ou o campo esteja vazio.
  let data: { gtm_id: string | null; ga4_id: string | null; google_ads_id: string | null; clarity_id: string | null } | null = null;
  try {
    const result = await supabase
      .from("site_settings")
      .select("gtm_id, ga4_id, google_ads_id, clarity_id")
      .eq("id", "default")
      .maybeSingle();
    data = result.data;
  } catch (err) {
    console.error("Falha ao buscar site_settings (usando IDs do código):", err);
  }

  return {
    gtmId: data?.gtm_id ?? TRACKING_IDS.gtmId ?? null,
    ga4Id: data?.ga4_id ?? TRACKING_IDS.ga4Id ?? null,
    googleAdsId: data?.google_ads_id ?? TRACKING_IDS.googleAdsId ?? null,
    clarityId: data?.clarity_id ?? TRACKING_IDS.clarityId ?? null,
  };
});
